/**
 * Feb 2026 v31.3 — Panel admin de Subastas en revisión.
 *
 * Lista todas las subastas en `pending_review` con las fotos, datos del
 * vendedor y botones de aprobar / rechazar. Montado dentro del AdminDashboard
 * (/admin/auctions) para mantener la UX consistente con el resto del panel.
 */
import { useEffect, useState } from "react";
import { api, cardImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useDialog } from "@/lib/dialog";
import { useAuth } from "@/lib/auth";
import { useLightbox } from "@/lib/lightbox";
import { AdminAuctionLifecycle } from "@/components/auction/AdminAuctionLifecycle";
import { HitSectionLoading, useLoadingLevel } from "@/components/loading";
import { Gavel, MapPin, Package, Clock, DollarSign, Globe, CheckCircle2, XCircle, Filter } from "lucide-react";

const GAME_LABEL = { pokemon: "Pokémon", yugioh: "Yu-Gi-Oh!", riftbound: "Riftbound", other: "Otro" };
const TYPE_LABEL = { card_single: "Carta individual", card_group: "Grupo de cartas", sealed: "Sellado" };
const STATUS_LABEL = {
  pending_review: "En revisión",
  active: "Activa",
  rejected: "Rechazada",
  finalized: "Finalizada",
  lifecycle: "Ciclo post-subasta",
};
const STATUS_COLOR = {
  pending_review: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  rejected: "bg-red-500/10 text-red-300 border-red-500/30",
  finalized: "bg-neutral-500/10 text-neutral-300 border-neutral-500/30",
};

export default function AdminSubastas() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const level = useLoadingLevel(loading);
  const busy = loading || level !== "none";
  const [filter, setFilter] = useState("pending_review");
  const { prompt, confirm } = useDialog();
  const { open: openImg } = useLightbox();
  const { user } = useAuth();
  const isSuper = user?.role === "admin";
  const payPending = (a) => a.transfer_verification_pending || (Number(a.day_fee_owed) > 0 && !a.day_fee_paid);

  const load = async () => {
    if (filter === "lifecycle") return;
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await api.get("/admin/auctions", { params });
      setList(r.data.results || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron cargar las subastas");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (id) => {
    const ok = await confirm({
      title: "Aprobar subasta",
      body: "La subasta pasa de 'En revisión' a 'Activa' y el contador de duración empieza AHORA. Los compradores verificados podrán pujar.",
      confirmLabel: "Aprobar",
    });
    if (!ok) return;
    try {
      const r = await api.post(`/admin/auctions/${id}/approve`);
      toast.success(r.data.message || (r.data.status === "active" ? "Subasta activa" : "Contenido aprobado"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo aprobar");
    }
  };

  const reject = async (id) => {
    const reason = await prompt({
      title: "Rechazar subasta",
      label: "Razón del rechazo (el vendedor la verá)",
      multiline: true,
      required: true,
      placeholder: "Fotos ilegibles, producto prohibido, descripción deshonesta…",
    });
    if (!reason) return;
    try {
      await api.post(`/admin/auctions/${id}/reject`, { reason });
      toast.success("Subasta rechazada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo rechazar");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Gavel size={20} className="text-[#EF4444]" />
          <h2 className="font-display text-xl">Subastas · {filter === "lifecycle" ? "Ciclo post-subasta" : "Revisión"}</h2>
          {filter !== "lifecycle" && <span className="text-xs text-neutral-500" data-testid="admin-auctions-count">({list.length})</span>}
        </div>
        <div className="flex items-center gap-1 flex-wrap text-xs">
          <Filter size={12} className="text-neutral-500" />
          {["pending_review", "active", "rejected", "all", "lifecycle"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`admin-auctions-filter-${s}`}
              className={`px-2.5 py-1 rounded border transition-colors ${filter === s ? "border-[#EF4444] text-white bg-[#EF4444]/10" : "border-[#262626] text-neutral-400 hover:text-white"}`}
            >
              {s === "all" ? "Todas" : STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      {filter === "lifecycle" && <AdminAuctionLifecycle />}
      {filter !== "lifecycle" && <>
      {busy && <HitSectionLoading level={level} loading={loading} section="admin" />}
      {!busy && list.length === 0 && (
        <div className="text-neutral-500 text-sm p-6 bg-[#111] border border-[#262626] rounded">
          Sin subastas en este filtro.
        </div>
      )}

      <div className="space-y-3">
        {list.map((a) => (
          <div key={a.id} data-testid={`admin-auction-row-${a.id}`} className="bg-[#111] border border-[#262626] rounded-lg p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg">{a.product_name}</h3>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_COLOR[a.status] || ""}`}>
                    {STATUS_LABEL[a.status] || a.status}
                  </span>
                </div>
                <div className="text-xs text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1"><Globe size={11} /> {GAME_LABEL[a.game_slug] || a.game_slug}{a.other_game_label ? ` · ${a.other_game_label}` : ""}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1"><Package size={11} /> {TYPE_LABEL[a.product_type] || a.product_type}</span>
                  <span>·</span>
                  <span>{a.condition}</span>
                  {a.rarity_text && <><span>·</span><span>{a.rarity_text}</span></>}
                  {a.set_name && <><span>·</span><span>{a.set_name}</span></>}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  Vendedor: <span className="text-neutral-300">{a.seller?.email || a.seller_id}</span>
                  {a.seller?.verified ? <span className="ml-1 text-emerald-400">✓ verificado</span> : <span className="ml-1 text-amber-400">⚠ no verificado</span>}
                </div>
              </div>
              <div className="flex gap-2">
                {(a.status === "pending_review") && payPending(a) && !isSuper && (
                  <span className="text-[11px] text-amber-300 border border-amber-500/40 rounded px-2 py-1" data-testid={`admin-auction-transfer-only-${a.id}`}>
                    Pago por verificar · solo súper admin
                  </span>
                )}
                {(a.status === "pending_review") && (isSuper || !payPending(a)) && (
                  <>
                    <Button data-testid={`admin-auction-approve-${a.id}`} onClick={() => approve(a.id)} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                      <CheckCircle2 size={14} className="mr-1" /> Aprobar
                    </Button>
                    <Button data-testid={`admin-auction-reject-${a.id}`} onClick={() => reject(a.id)} size="sm" variant="outline" className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10">
                      <XCircle size={14} className="mr-1" /> Rechazar
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Fotos */}
            {Array.isArray(a.photos) && a.photos.length > 0 && (
              <div className="flex gap-2 overflow-x-auto mb-3">
                {a.photos.map((p, i) => (
                  <button
                    key={`${a.id}-ph-${i}`}
                    data-testid={`admin-auction-photo-${a.id}-${i}`}
                    onClick={() => openImg(a.photos.map((pp) => ({ path: pp, scope: "public" })), i)}
                    className="flex-none w-24 h-24 rounded border border-[#262626] overflow-hidden bg-[#0a0a0a]"
                  >
                    <img src={cardImageUrl(p)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
                  </button>
                ))}
              </div>
            )}

            {/* Datos */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <div className="flex items-center gap-1.5 text-neutral-400">
                <DollarSign size={12} /> Precio salida: <span className="text-white font-medium">${Number(a.starting_price).toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-neutral-400">
                <Clock size={12} /> {a.duration_days} día{a.duration_days > 1 ? "s" : ""}
                {Number(a.day_fee_owed) > 0 && <span className="text-amber-400 ml-1">· fee ${Number(a.day_fee_owed).toFixed(2)}{a.day_fee_paid ? " ✓" : " pendiente"}</span>}
              </div>
              <div className="flex items-center gap-1.5 text-neutral-400">
                <MapPin size={12} /> {a.city}, {a.province}
              </div>
              <div className="text-neutral-400">
                Envío misma prov.: <span className="text-white">${Number(a.shipping_cost_same_province || 0).toFixed(2)}</span>
                {" · "}
                otras prov.: <span className="text-white">${Number(a.shipping_cost_other_province || 0).toFixed(2)}</span>
              </div>
              <div className="text-neutral-400 sm:col-span-2">
                Entrega personal: <span className="text-white">{a.accepts_local_delivery ? `Sí (${a.meetup_location || "sin punto"})` : "No"}</span>
              </div>
            </div>

            {a.description && (
              <div className="mt-3 text-xs text-neutral-300 bg-[#0a0a0a] border border-[#262626] rounded p-2 whitespace-pre-wrap">
                {a.description}
              </div>
            )}

            {a.status === "rejected" && a.reject_reason && (
              <div className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2">
                <b>Rechazo:</b> {a.reject_reason}
              </div>
            )}
          </div>
        ))}
      </div>
      </>}
    </div>
  );
}
